import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Sidebar from '../../../components/feature/Sidebar';
import VideoEmbed from '../components/VideoEmbed';
import TroubleChecklist from './components/TroubleChecklist';
import DiagnosticFlowchart from './components/DiagnosticFlowchart';
import ContentRenderer from '../../article/components/ContentRenderer';
import { supabase, BlogPost } from '../../../lib/supabase';

interface FAQItem {
  question: string;
  answer: string;
}

interface TOCItem {
  id: string;
  text: string;
  level: number;
}

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogPost | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(null);
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isTocOpen, setIsTocOpen] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  // メタタグを動的に設定
  useEffect(() => {
    if (article) {
      // タイトル
      document.title = (article as any).meta_title || article.title;
      
      // メタディスクリプション
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', (article as any).meta_description || article.excerpt || '');

      // キーワード
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      const keywords = (article as any).keywords as string[] | undefined;
      if (keywords && keywords.length > 0) {
        metaKeywords.setAttribute('content', keywords.join(', '));
      } else {
        metaKeywords.setAttribute('content', '');
      }

      // OGPタグ
      const setOGTag = (property: string, content: string) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      setOGTag('og:title', (article as any).meta_title || article.title);
      setOGTag('og:description', (article as any).meta_description || article.excerpt || '');
      setOGTag('og:image', (article as any).og_image || article.image);
      setOGTag('og:type', 'article');
      setOGTag('og:url', window.location.href);

      // Twitterカード
      const setTwitterTag = (name: string, content: string) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('name', name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      setTwitterTag('twitter:card', 'summary_large_image');
      setTwitterTag('twitter:title', (article as any).meta_title || article.title);
      setTwitterTag('twitter:description', (article as any).meta_description || article.excerpt || '');
      setTwitterTag('twitter:image', (article as any).og_image || article.image);
    }

    return () => {
      // クリーンアップ時にデフォルトに戻す
      document.title = 'HR Tech Blog';
    };
  }, [article]);

  // 目次を生成
  useEffect(() => {
    if (article && contentRef.current) {
      // DOMが完全にレンダリングされるまで待つ
      const timer = setTimeout(() => {
        if (!contentRef.current) return;
        
        // コンテンツから見出しを抽出
        const headings = contentRef.current.querySelectorAll('h2, h3, h4');
        const items: TOCItem[] = [];
        
        headings.forEach((heading, index) => {
          const id = `heading-${index}`;
          heading.id = id;
          items.push({
            id,
            text: heading.textContent || '',
            level: heading.tagName === 'H2' ? 2 : heading.tagName === 'H3' ? 3 : 4
          });
        });
        
        setTocItems(items);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [article, loading]);

  // スクロール監視で現在の見出しをハイライト
  useEffect(() => {
    if (tocItems.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -80% 0px' }
    );

    tocItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [tocItems]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      
      const { data: articleData, error: articleError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();

      if (articleError) throw articleError;
      
      if (articleData) {
        setArticle(articleData);

        const { data: relatedData, error: relatedError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('category', articleData.category)
          .eq('is_published', true)
          .neq('id', articleData.id)
          .order('published_date', { ascending: false })
          .limit(2);

        if (relatedError) throw relatedError;
        setRelatedArticles(relatedData || []);
      } else {
        setArticle(null);
      }
    } catch (error) {
      console.error('記事の取得に失敗しました:', error);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  // FAQスキーマを生成
  const generateFAQSchema = (faqs: FAQItem[]) => {
    if (!faqs || faqs.length === 0) return null;
    
    const validFaqs = faqs.filter(f => f.question && f.answer);
    if (validFaqs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": validFaqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  };

  // 記事スキーマを生成
  const generateArticleSchema = () => {
    if (!article) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": (article as any).meta_description || article.excerpt,
      "image": (article as any).og_image || article.image,
      "author": {
        "@type": "Person",
        "name": article.author || "編集部"
      },
      "publisher": {
        "@type": "Organization",
        "name": "HR Tech Blog"
      },
      "datePublished": article.published_date,
      "dateModified": article.updated_at || article.published_date
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.');
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 text-cyan-500">
            <i className="ri-loader-4-line text-6xl animate-spin"></i>
          </div>
          <p className="text-gray-600">読み込み中...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 text-gray-300">
            <i className="ri-article-line text-6xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">記事が見つかりません</h1>
          <p className="text-gray-600 mb-8">お探しの記事は存在しないか、削除された可能性があります。</p>
          <Link
            to="/blog"
            className="inline-flex items-center px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            ブログ一覧に戻る
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Schema.org 構造化データ */}
      {article && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(generateArticleSchema()) }}
          />
          {(article as any).faqs && (article as any).faqs.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFAQSchema((article as any).faqs)) }}
            />
          )}
        </>
      )}

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* パンくずリスト */}
        <nav className="mb-6">
          <ol className="flex items-center text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-cyan-500 cursor-pointer">ホーム</Link>
            </li>
            <li className="mx-2">/</li>
            <li>
              <Link to="/blog" className="hover:text-cyan-500 cursor-pointer">ブログ</Link>
            </li>
            <li className="mx-2">/</li>
            <li className="text-gray-900 truncate max-w-xs">{article.title}</li>
          </ol>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* メインコンテンツ */}
          <div className="flex-1">
            <article className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* ヘッダー画像 */}
              <div className="w-full h-96 md:h-[500px] bg-gray-200">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="p-6 md:p-8">
                {/* メタ情報 */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-cyan-500 text-white text-sm px-3 py-1 rounded-full font-medium">
                    {article.category}
                  </span>
                  <time className="text-gray-500 text-sm">{formatDate(article.published_date)}</time>
                  <div className="flex items-center text-gray-500 text-sm">
                    <i className="ri-time-line mr-1"></i>
                    {article.reading_time}分
                  </div>
                </div>

                {/* タイトル */}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-tight">
                  {article.title}
                </h1>

                {/* 目次 */}
                {tocItems.length > 0 && (
                  <div className="mb-8 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setIsTocOpen(!isTocOpen)}
                      className="w-full px-5 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-150 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center text-cyan-500">
                          <i className="ri-list-ordered text-lg"></i>
                        </div>
                        <span className="font-bold text-gray-900">目次</span>
                        <span className="text-sm text-gray-500">（{tocItems.length}項目）</span>
                      </div>
                      <div className={`w-6 h-6 flex items-center justify-center text-gray-500 transition-transform duration-300 ${isTocOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-xl"></i>
                      </div>
                    </button>
                    <div className={`transition-all duration-300 ${isTocOpen ? 'max-h-[800px] overflow-y-auto' : 'max-h-0 overflow-hidden'}`}>
                      <nav className="p-4">
                        <ul className="space-y-1">
                          {tocItems.map((item) => (
                            <li key={item.id}>
                              <button
                                onClick={() => scrollToHeading(item.id)}
                                type="button"
                                className={`w-full text-left py-2 px-3 rounded-md transition-all duration-200 cursor-pointer flex items-start gap-2 ${
                                  item.level === 3 ? 'ml-4' : ''
                                } ${
                                  activeId === item.id
                                    ? 'bg-cyan-100 text-cyan-600 font-medium'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                              >
                                <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-xs ${
                                  activeId === item.id ? 'bg-cyan-500 text-white' : 'bg-gray-200 text-gray-500'
                                }`}>
                                  {item.level === 2 ? <i className="ri-h-2"></i> : <i className="ri-h-3"></i>}
                                </span>
                                <span className="text-sm leading-relaxed">{item.text}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}

                {/* 動画埋め込み */}
                {article.video_url && article.video_type && (
                  <VideoEmbed
                    url={article.video_url}
                    type={article.video_type as 'youtube' | 'vimeo'}
                    title={article.title}
                  />
                )}

                {/* 本文 */}
                <div ref={contentRef}>
                  <ContentRenderer content={article.content} slug={article.slug} />
                </div>

                {/* 配信トラブルチェックリスト */}
                <TroubleChecklist />

                {/* トラブル診断フローチャート */}
                <DiagnosticFlowchart />

                {/* FAQセクション */}
                {(article as any).faqs && (article as any).faqs.length > 0 && (
                  <div className="mt-12 pt-8 border-t border-gray-200">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 flex items-center justify-center bg-cyan-100 rounded-full">
                        <i className="ri-question-answer-line text-cyan-500"></i>
                      </div>
                      <h2 className="text-xl font-bold text-gray-900">よくある質問</h2>
                    </div>
                    <div className="space-y-3">
                      {((article as any).faqs as FAQItem[]).filter(f => f.question && f.answer).map((faq, index) => (
                        <div 
                          key={index} 
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleFAQ(index)}
                            className="w-full px-5 py-4 flex items-center justify-between text-left bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                          >
                            <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                            <div className={`w-6 h-6 flex items-center justify-center text-gray-500 transition-transform ${openFAQIndex === index ? 'rotate-180' : ''}`}>
                              <i className="ri-arrow-down-s-line text-xl"></i>
                            </div>
                          </button>
                          <div 
                            className={`overflow-hidden transition-all duration-300 ${
                              openFAQIndex === index ? 'max-h-96' : 'max-h-0'
                            }`}
                          >
                            <div className="px-5 py-4 bg-white text-gray-700 leading-relaxed">
                              {faq.answer}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* シェアボタン */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <p className="text-gray-700 font-medium mb-4">この記事をシェア</p>
                  <div className="flex gap-3">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full hover:bg-[#1DA1F2] hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-twitter-x-line"></i>
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full hover:bg-[#1877F2] hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-facebook-fill"></i>
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full hover:bg-[#0A66C2] hover:text-white transition-colors cursor-pointer"
                    >
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert('URLをコピーしました');
                      }}
                      className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-link"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* 関連記事 */}
            {relatedArticles.length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">関連記事</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/blog/${related.slug}`}
                      className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group cursor-pointer"
                    >
                      <div className="w-full h-40 bg-gray-200 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-cyan-500 text-xs font-medium">{related.category}</span>
                        <h3 className="text-gray-900 font-semibold mt-1 line-clamp-2 group-hover:text-cyan-500 transition-colors">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* ブログ一覧へ戻る */}
            <div className="mt-8 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer font-medium whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                ブログ一覧に戻る
              </Link>
            </div>
          </div>

          {/* サイドバー */}
          <div className="lg:flex-shrink-0">
            <Sidebar />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
